'use client'

import type { HeadingBlockProps } from '@/blocks/HeadingBlock/HeadingBlock'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import { ImageWithPlaceholder } from '@/components/ImageWithPlaceholder/ImageWithPlaceholder'
import type {
  Item as StoryCarouselItem,
  StoryAspectRatio,
  StoryVariant,
} from '@/components/StoryCarousel/StoryCarousel.types'
import { StoryCarouselDesktop } from '@/components/StoryCarousel/StoryCarouselDesktop'
import { StoryCarouselMobile } from '@/components/StoryCarousel/StoryCarouselMobile'
import type {
  ExpectedBlockType,
  SbBaseBlockProps,
  StoryblokAsset,
  StoryblokTagsPlugin,
} from '@/services/storyblok/storyblok'
import { useBreakpoint } from '@/utils/useBreakpoint/useBreakpoint'
import { type SbBlokData, storyblokEditable } from '@storyblok/react'
import { xStack } from 'ui/src/patterns/stack'
import { backgroundImage as backgroundImageStyle, root } from './StoryCarouselBlock.css'

const IMAGE_ITEM_BLOCK_NAME = 'storyCarouselImageItem'
const VIDEO_ITEM_BLOCK_NAME = 'storyCarouselVideoItem'
const TEXT_ITEM_BLOCK_NAME = 'storyCarouselTextItem'

type StoryCarouselImageItemBlock = SbBlokData & {
  component: typeof IMAGE_ITEM_BLOCK_NAME
  asset: StoryblokAsset
  title?: string
  subtitle?: string
  tags?: StoryblokTagsPlugin
}

type StoryCarouselVideoItemBlock = SbBlokData & {
  component: typeof VIDEO_ITEM_BLOCK_NAME
  videoUrlWebm: string
  title?: string
  subtitle?: string
  videoUrl?: string
  poster?: StoryblokAsset
  tags?: StoryblokTagsPlugin | Array<string>
  loop?: boolean
}

type StoryCarouselTextItemBlock = SbBlokData & {
  component: typeof TEXT_ITEM_BLOCK_NAME
  subtitle?: string
  heading: ExpectedBlockType<HeadingBlockProps>
  tags?: StoryblokTagsPlugin | Array<string>
}

type StoryCarouselItemBlock = StoryCarouselImageItemBlock | StoryCarouselVideoItemBlock | StoryCarouselTextItemBlock

type StoryCarouselBlockProps = SbBaseBlockProps<{
  items: Array<StoryCarouselItemBlock>
  backgroundImage?: StoryblokAsset
  backgroundImagePriority?: boolean
  aspectRatio?: StoryAspectRatio
  storyVariant?: StoryVariant
  fadeInAnimation?: boolean
}>

export function StoryCarouselBlock({ blok }: StoryCarouselBlockProps) {
  const isDesktop = useBreakpoint('md')
  const aspectRatio = blok.aspectRatio ?? '3 / 4'
  const backgroundImage = blok.backgroundImage?.filename ? blok.backgroundImage : undefined

  const items = blok.items
    .filter((item) => isStoryCarouselImageItem(item) || isStoryCarouselVideoItem(item) || isStoryCarouselTextItem(item))
    .map(carouselItemBlockToStoryCarouselItem)

  if (items.length === 0) {
    console.log('[StoryCarouselBlock] No items found in block. Skipping rendering.')
    return null
  }

  const carousel = isDesktop ? (
    <StoryCarouselDesktop
      items={items}
      aspectRatio={aspectRatio}
      storyVariant={blok.storyVariant}
      fadeInAnimation={blok.fadeInAnimation}
    />
  ) : (
    <StoryCarouselMobile items={items} aspectRatio={aspectRatio} storyVariant={blok.storyVariant} />
  )

  if (!backgroundImage) {
    if (isDesktop) {
      return carousel
    }

    return (
      <GridLayout.Root key={blok._uid} {...storyblokEditable(blok)}>
        <GridLayout.Content className={xStack({ justifyContent: 'center' })} width="1">
          {carousel}
        </GridLayout.Content>
      </GridLayout.Root>
    )
  }

  return (
    <section key={blok._uid} className={root} {...storyblokEditable(blok)}>
      <ImageWithPlaceholder
        className={backgroundImageStyle}
        src={backgroundImage.filename}
        alt=""
        fill
        imagePriority={blok.backgroundImagePriority}
      />
      {isDesktop ? (
        // The desktop carousel is full-bleed: it spans the full width and aligns cards to the
        // content area internally, so it must not be constrained by GridLayout or cards get clipped.
        carousel
      ) : (
        <GridLayout.Root>
          <GridLayout.Content className={xStack({ justifyContent: 'center', paddingInline: 'md' })} width="1">
            {carousel}
          </GridLayout.Content>
        </GridLayout.Root>
      )}
    </section>
  )
}

function isStoryCarouselImageItem(blok: StoryCarouselItemBlock): blok is StoryCarouselImageItemBlock {
  return blok.component === IMAGE_ITEM_BLOCK_NAME
}

function isStoryCarouselVideoItem(blok: StoryCarouselItemBlock): blok is StoryCarouselVideoItemBlock {
  return blok.component === VIDEO_ITEM_BLOCK_NAME
}

function isStoryCarouselTextItem(blok: StoryCarouselItemBlock): blok is StoryCarouselTextItemBlock {
  return blok.component === TEXT_ITEM_BLOCK_NAME
}

function carouselItemBlockToStoryCarouselItem(blok: StoryCarouselItemBlock): StoryCarouselItem {
  const tags = getStoryCarouselItemTags(blok.tags)

  switch (blok.component) {
    case TEXT_ITEM_BLOCK_NAME:
      return {
        id: blok._uid || crypto.randomUUID(),
        subtitle: blok.subtitle,
        heading: blok.heading,
        tags,
      }
    case IMAGE_ITEM_BLOCK_NAME:
      return {
        id: blok._uid || crypto.randomUUID(),
        asset: {
          type: 'image',
          src: blok.asset.filename,
          alt: blok.asset.alt,
        },
        title: blok.title,
        subtitle: blok.subtitle,
        tags,
      }
    case VIDEO_ITEM_BLOCK_NAME: {
      const sources: Array<{ url: string }> = []
      // Order is important here; 'videoUrl' might contain MP4 video with alpha channel so it should came first
      const providedVideoUrls = [blok.videoUrl, blok.videoUrlWebm].filter(isValidUrl)
      providedVideoUrls.forEach((url) => sources.push({ url }))

      return {
        id: blok._uid || crypto.randomUUID(),
        asset: {
          type: 'video',
          sources,
          poster: blok.poster?.filename,
          loop: blok.loop,
        },
        title: blok.title,
        subtitle: blok.subtitle,
        tags,
      }
    }
  }
}

function getStoryCarouselItemTags(tags?: StoryblokTagsPlugin | Array<string>): StoryCarouselItem['tags'] {
  return Array.isArray(tags) ? tags : tags?.value
}

function isValidUrl(url?: string): url is string {
  return typeof url === 'string' && url.trim().length > 0
}
